import assign from 'lodash/assign';
import remove from 'lodash/remove';
import moment from '@/utils/moment-wrapper.ts';
import {
    fetchConversation,
    fetchUserConversations,
    fetchConversationFilePage as fetchConversationFilePageApi,
    getAskPendoPostRequest,
    getInitialState,
    cleanupIncompleteStoppedMessages,
    parseMessageContent,
    postReportTrigger,
} from './ask-pendo.utils.js';
import { StreamingProcessor } from './ask-pendo.streaming-processor.js';
import { startPollingReport } from './report-polling.js';

// Module-private registry of poll-cancel functions keyed by
// `${conversationId}::${reportId}`. Lives outside Vuex state because cancel
// fns are non-serializable (functions hold closures over timers). Vuex state
// still owns the *list* of pending reports (what UI cares about); this map
// owns the *machinery* (how we stop polling).
const pollCancellers = new Map();
function pollKey (conversationId, reportId) {
    return `${conversationId}::${reportId}`;
}

const askPendo = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        addMessage (state, { conversationId, type, content, timestamp, metadata = {}, data = {}, streamId = null }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            const message = {
                type,
                content,
                timestamp,
                metadata,
                ...data,
            };

            // Add streamId if provided
            if (streamId) {
                message.streamId = streamId;
            }

            conversation.messages.push(message);

            // Clean up redundant stopped messages
            cleanupIncompleteStoppedMessages(conversation.messages);
        },
        addStateToConversation (state, { conversationId, fieldName, fieldData }) {
            const conversation = state.conversations[conversationId];
            conversation.state_details[fieldName] = fieldData;
        },
        clearActiveStreamId (state, { conversationId }) {
            if (state.activeStreamIds) {
                delete state.activeStreamIds[conversationId];
            }
        },
        clearAbortController (state, { conversationId }) {
            delete state.abortControllers[conversationId];
        },
        clearArtifactMenuItems (state, { artifactId }) {
            delete state.artifactMenuItems[artifactId];
        },
        removeLastExchange (state, { conversationId }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            const { messages, artifacts } = conversation;

            // find the last user message index
            let lastUserIndex = -1;
            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i]?.type === 'user') {
                    lastUserIndex = i;
                    break;
                }
            }
            if (lastUserIndex === -1) return; // nothing to remove

            // collect artifact ids from messages being removed
            const removedArtifactIds = new Set();
            for (let i = lastUserIndex; i < messages.length; i++) {
                const ids = messages[i]?.metadata?.artifact_ids;
                if (Array.isArray(ids)) {
                    for (const id of ids) removedArtifactIds.add(id);
                }
            }

            // remove everything from last user message onward
            messages.length = lastUserIndex; // truncate in place

            // delete associated artifacts
            if (artifacts && removedArtifactIds.size) {
                for (const id of removedArtifactIds) {
                    delete artifacts[id];
                }
            }

            // clear last_artifact_id if it was removed
            if (conversation.last_artifact_id && removedArtifactIds.has(conversation.last_artifact_id)) {
                delete conversation.last_artifact_id;
            }
        },
        removeIncompleteMessages (state, { conversationId, streamId = null }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            const { messages } = conversation;
            const isBlank = (item) => !item || item.trim() === '';

            // Helper: Check if message is incomplete assistant
            const isIncompleteAssistant = (msg, targetStreamId) =>
                msg.type === 'assistant' &&
                msg.tool_calls?.length > 0 &&
                isBlank(parseMessageContent(msg).content) &&
                (!targetStreamId || msg.streamId === targetStreamId);

            // Helper: Clean assistant message
            const cleanAssistant = (msg) => {
                const cleaned = { ...msg };
                delete cleaned.tool_calls;
                if (isBlank(parseMessageContent(cleaned).content)) cleaned.content = 'Request was stopped by the user.';

                return cleaned;
            };

            const toolCallIds = new Set();

            // First pass: find incomplete assistant and collect tool call IDs
            for (let i = messages.length - 1; i >= 0; i--) {
                const msg = messages[i];
                if (isIncompleteAssistant(msg, streamId)) {
                    for (const tc of msg.tool_calls) toolCallIds.add(tc.id);
                    messages[i] = cleanAssistant(msg);
                    break; // Only process the first (last) incomplete assistant found
                }
            }

            // Second pass: remove tool messages if we found any tool call IDs
            if (toolCallIds.size > 0) {
                remove(messages, (msg) => msg && msg.type === 'tool' && toolCallIds.has(msg.tool_call_id));
            }
            // tidy up any redundant "stopped" right before a real assistant message
            cleanupIncompleteStoppedMessages(messages);
        },
        setAbortController (state, { conversationId, controller }) {
            state.abortControllers[conversationId] = controller;
        },
        setActiveStreamId (state, { conversationId, streamId }) {
            state.activeStreamIds[conversationId] = streamId;
        },
        setArtifactConfig (state, { artifactId, config }) {
            state.artifactConfig[artifactId] = assign({}, state.artifactConfig[artifactId], config);
        },
        setArtifactMenuItems (state, { artifactId, items }) {
            state.artifactMenuItems[artifactId] = items;
        },
        setConfigDrawerState (state, { isOpen }) {
            state.configDrawerOpen = isOpen;
        },
        setConversation (state, { id, conversation }) {
            state.conversations[id] = conversation;
        },
        setConversationUserContext (state, { id, userContext }) {
            if (state.conversations[id]) {

                state.conversations[id].user_context = userContext;
            }
        },
        setUserConversations (state, { conversations }) {
            state.userConversations = conversations;
        },
        setRecentConversations (state, { conversations }) {
            state.recentConversations = conversations;
        },
        setEmployeeConfig (state, { employeeConfig }) {
            state.employeeConfig = employeeConfig;
        },
        setCustomInstructions (state, { customInstructions }) {
            state.customInstructions = customInstructions;
        },
        setResponsePending (state, { id, isPending }) {
            state.pendingResponses[id] = isPending;
        },
        setWorkflowState (state, { conversationId, workflowState }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            conversation.workflow_state = workflowState;
        },
        setPlanState (state, { conversationId, planState }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            conversation.plan = planState;
        },
        updateTodo (state, { conversationId, updatedTodo }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            const todos = conversation.plan.todos.map((t) => (t.id === updatedTodo.id ? updatedTodo : t));
            conversation.plan = { ...conversation.plan, todos };
        },
        addPendingReport (state, { conversationId, entry }) {
            if (!conversationId || !entry?.report_id) return;
            if (!state.pendingReports[conversationId]) {
                state.pendingReports[conversationId] = {};
            }
            // Idempotent — first observation wins; later duplicates (e.g. the
            // `final_state.pending_reports` echo after the in-stream chunk)
            // are no-ops.
            if (!state.pendingReports[conversationId][entry.report_id]) {
                state.pendingReports[conversationId][entry.report_id] = { ...entry };
            }
        },
        removePendingReport (state, { conversationId, reportId }) {
            const bucket = state.pendingReports[conversationId];
            if (!bucket) return;
            delete bucket[reportId];
            if (Object.keys(bucket).length === 0) {
                delete state.pendingReports[conversationId];
            }
        },
        setPendingReports (state, { conversationId, entries }) {
            if (!conversationId) return;
            const bucket = {};
            for (const entry of (entries || [])) {
                if (entry?.report_id) bucket[entry.report_id] = { ...entry };
            }
            if (Object.keys(bucket).length === 0) {
                delete state.pendingReports[conversationId];
            } else {
                state.pendingReports[conversationId] = bucket;
            }
        },
        updateArtifact (state, { conversationId, artifactId, updates }) {
            const conversation = state.conversations[conversationId];
            if (!conversation?.artifacts?.[artifactId]) return;

            const artifact = conversation.artifacts[artifactId];

            artifact.data = {
                ...artifact.data,
                ...updates,
            };
        },
    },
    actions: {
        addMessage ({ commit }, { conversationId, type, content, metadata = {}, data = {}, streamId = null }) {
            commit('addMessage', {
                conversationId,
                type,
                content,
                timestamp: moment().toISOString(),
                metadata,
                data,
                streamId,
            });
        },
        async createConversation ({ commit }, {
            id,
            prompt,
            type = 'user',
            metadata = {},
            artifacts = {},
            // eslint-disable-next-line camelcase
            user_context = {},
            files = {},
            suggestedPrompt = false,
        }) {
            const conversation = {
                conversationId: id,
                messages: [],
                artifacts,
                // eslint-disable-next-line camelcase
                user_context,
                files,
                suggestedPrompt,
            };

            commit('setConversation', { id, conversation });

            const hasPrompt = typeof prompt === 'string' && prompt.trim().length > 0;
            if (hasPrompt) {
                commit('addMessage', {
                    conversationId: id,
                    type,
                    content: prompt,
                    timestamp: moment().toISOString(),
                    metadata,
                });
            }

            return conversation;
        },
        clearArtifactMenuItems ({ commit }, artifactId) {
            commit('clearArtifactMenuItems', { artifactId });
        },
        async loadAll ({ state, commit }, { reFetch = false } = {}) {
            if (state.userConversations.length > 0 && !reFetch) {
                return state.userConversations;
            }

            const { data } = await fetchUserConversations();
            commit('setUserConversations', { conversations: data });

            return data;
        },
        async loadRecent ({ state, commit }, { reFetch = false, limit = 3 } = {}) {
            if (state.recentConversations.length > 0 && !reFetch) {
                return state.recentConversations;
            }

            const { data } = await fetchUserConversations({ limit });
            commit('setRecentConversations', { conversations: data });

            return data;
        },
        async loadOne ({ commit, dispatch, getters }, { id, reFetch = false }) {
            if (!reFetch && getters.conversationById(id)) {
                return Promise.resolve();
            }

            try {
                const { data } = await fetchConversation(id);
                commit('setConversation', {
                    id: data.conversationId,
                    conversation: data,
                });

                // Resume polling for any long-running Reports that were
                // submitted before the user navigated away (US3). The
                // pendo-appengine proxy hydrates `pending_reports` onto the
                // public conversation shape — see APP-155825.
                if (Array.isArray(data.pending_reports) && data.pending_reports.length > 0) {
                    dispatch('hydratePendingReports', {
                        conversationId: data.conversationId,
                        entries: data.pending_reports,
                    });
                }

                return data;
            } catch (error) {
                if (error.response?.status === 403 && error.response?.data?.reason === 'app_entitlement') {
                    error.status = 403;
                    error.reason = 'app_entitlement';
                    error.restrictedApps = error.response.data.restrictedApps;
                }
                throw error;
            }
        },
        removeLastExchange ({ commit }, { conversationId }) {
            commit('removeLastExchange', { conversationId });
        },
        async sendConversation (
            { commit, state, dispatch, rootState },
            { id, type, revision, suggestedPrompt = false, onStreamRead = () => {}, onStreamEnd = () => {} },
        ) {
            dispatch('startLoading', id);
            const conversation = state.conversations[id];

            if (!conversation) {
                dispatch('stopLoading', id);
                throw new Error('Conversation not found');
            }

            const postRequest = getAskPendoPostRequest(type);
            if (!postRequest) {
                dispatch('stopLoading', id);
                throw new Error('Invalid conversation type');
            }

            // Cancel any existing stream for this conversation
            const existingController = state.abortControllers[conversation.conversationId];
            if (existingController) {
                existingController.abort();
                commit('clearAbortController', { conversationId: conversation.conversationId });
            }

            // Create new AbortController for this request
            const abortController = new AbortController();
            commit('setAbortController', { conversationId: conversation.conversationId, controller: abortController });

            try {
                const data = await postRequest(
                    conversation,
                    state.employeeConfig,
                    { revision, signal: abortController.signal },
                    rootState?.auth?.user?.jobRole || '',
                    state.customInstructions || '',
                    suggestedPrompt,
                );

                if (data instanceof ReadableStream) {
                    let stringBuffer = '';

                    // Record when this stream started
                    const streamStartTime = Date.now();
                    const streamId = `${conversation.conversationId}_${streamStartTime}`;

                    // Mark this as the active stream for this conversation
                    // This immediately invalidates ALL previous streams for this conversation
                    commit('setActiveStreamId', { conversationId: conversation.conversationId, streamId });

                    const streamingProcessor = new StreamingProcessor({
                        onData: (chunk) => {
                            const currentActiveStreamId = state.activeStreamIds[conversation.conversationId];
                            if (currentActiveStreamId !== streamId) {
                                return;
                            }
                            switch (chunk.type) {
                                case 'assistant':
                                    if (chunk.tool_calls.length > 0) {
                                        dispatch('addMessage', {
                                            conversationId: conversation.conversationId,
                                            type: 'assistant',
                                            content: '',
                                            data: chunk,
                                            streamId,
                                        });
                                    }
                                    stringBuffer += chunk.content;
                                    break;
                                case 'tool':
                                    dispatch('addMessage', {
                                        conversationId: conversation.conversationId,
                                        type: 'tool',
                                        content: chunk.content,
                                        data: chunk,
                                        streamId,
                                    });
                                    break;
                                case 'workflow_state':
                                    commit('setWorkflowState', {
                                        conversationId: conversation.conversationId,
                                        workflowState: chunk.workflow,
                                    });
                                    break;
                                case 'plan_state':
                                    commit('setPlanState', {
                                        conversationId: conversation.conversationId,
                                        planState: chunk.plan,
                                    });
                                    break;
                                case 'todo_update':
                                    commit('updateTodo', {
                                        conversationId: conversation.conversationId,
                                        updatedTodo: chunk.todo,
                                    });
                                    break;
                                case 'pending_report':
                                    // Long-running Report submitted by a tool
                                    // (path/funnel/retention/AI Agent).
                                    // INTENTIONALLY a no-op: both the state
                                    // record AND the polling activation are
                                    // deferred to the `done` chunk's
                                    // `reconcilePendingReports` call. The
                                    // proxy persists `pending_reports`
                                    // synchronously on the done chunk; if we
                                    // start polling here, the FE can detect
                                    // a cached agg's "done" state and fire
                                    // the synthesis trigger before the
                                    // persist lands in GCS, leaving the
                                    // trigger handler with stale state.
                                    break;
                            }
                            onStreamRead({ ...chunk, streamId });
                        },
                        onEnd: (finalState) => {
                            const activeStreamId = state.activeStreamIds[conversation.conversationId];
                            const isActiveStream = activeStreamId === streamId;

                            // Handle case where finalState is null (e.g., when request is aborted)
                            if (!finalState || !stringBuffer || !isActiveStream) {
                                onStreamEnd();

                                return;
                            }

                            commit('setConversation', {
                                id: conversation.conversationId,
                                conversation: { conversationId: conversation.conversationId, ...finalState },
                            });

                            // Reconcile pending_reports with the authoritative
                            // server view (set semantics, keyed by report_id).
                            // The in-stream `pending_report` chunk is the
                            // latency optimisation; this is the durability
                            // guarantee. See pending-reports-chunk.md.
                            if (Array.isArray(finalState.pending_reports)) {
                                dispatch('reconcilePendingReports', {
                                    conversationId: conversation.conversationId,
                                    entries: finalState.pending_reports,
                                });
                            }

                            commit('clearActiveStreamId', { conversationId: conversation.conversationId });
                            onStreamEnd();
                        },
                        onError: (error) => {
                            throw error;
                        },
                    });

                    await streamingProcessor.process(data);

                    return;
                }

                if (data.body.error) {
                    dispatch('stopLoading', id);
                    throw new Error(data.body.error);
                }

                const conversationResponse = {
                    id,
                    ...data.body,
                    messages: data.body.messages.map((msg) => ({
                        ...msg,
                        timestamp: moment().toISOString(),
                    })),
                };
                commit('setConversation', { id, conversation: conversationResponse });
            } catch (error) {
                // Handle AbortError gracefully - don't show error to user when they click stop
                if (error.name === 'AbortError' || error.name === 'CanceledError' || error.message.includes('aborted')) {
                    return; // Silent return, no error shown to user
                }
                throw new Error(
                    `An error occurred while generating the response. Try opening a new conversation.\n\nError: ${error.message}`,
                );
            } finally {
                dispatch('stopLoading', id);
                commit('clearAbortController', { conversationId: conversation.conversationId });
            }
        },
        setArtifactConfig ({ commit }, { artifactId, config }) {
            commit('setArtifactConfig', { artifactId, config });
        },
        setArtifactMenuItems ({ commit }, { artifactId, items }) {
            commit('setArtifactMenuItems', { artifactId, items });
        },
        async fetchConversationFilePage (_context, { conversationId, fileId, pageToken, pageSize } = {}) {
            return fetchConversationFilePageApi(conversationId, fileId, { pageToken, pageSize });
        },
        setEmployeeConfigProperty ({ commit, state }, { key, value }) {
            const employeeConfig = {
                ...state.employeeConfig,
                [key]: value,
            };
            commit('setEmployeeConfig', { employeeConfig });
        },
        // Removes a key entirely from employeeConfig. Use instead of
        // setEmployeeConfigProperty({key, value: null}) when the outgoing
        // payload must omit the field rather than send it as null — e.g.
        // the APP-155465 reranking-strategy dropdown's "Default" option,
        // which must produce byte-identical request bodies to today's
        // baseline behavior.
        unsetEmployeeConfigProperty ({ commit, state }, key) {
            const { [key]: _removed, ...employeeConfig } = state.employeeConfig;
            commit('setEmployeeConfig', { employeeConfig });
        },
        updateCustomInstructions ({ commit }, customInstructions) {
            commit('setCustomInstructions', { customInstructions: customInstructions || '' });
        },
        startLoading ({ commit }, conversationId) {
            commit('setResponsePending', { id: conversationId, isPending: true });
        },
        stopLoading ({ commit }, conversationId) {
            commit('setResponsePending', { id: conversationId, isPending: false });
        },
        stopResponse ({ dispatch, commit, state }, { conversationId }) {
            // Get the active stream ID before clearing it so we can target the right incomplete messages
            const activeStreamId = state.activeStreamIds[conversationId];

            // Abort the actual HTTP request using AbortController
            const controller = state.abortControllers[conversationId];
            if (controller) {
                controller.abort();
                commit('clearAbortController', { conversationId });
            }

            commit('clearActiveStreamId', { conversationId });
            // Clean up incomplete assistant messages to prevent chat history validation errors
            // Pass the streamId so we only clean up messages from the stream that was just stopped
            // Only run cleanup if we have a valid streamId to target
            if (activeStreamId) {
                commit('removeIncompleteMessages', { conversationId, streamId: activeStreamId });
            }
            dispatch('stopLoading', conversationId);
        },
        updateConfigDrawerState ({ commit }, { isOpen }) {
            commit('setConfigDrawerState', { isOpen });
        },
        /**
         * Track a single pending Report and (re)start polling for it.
         * Idempotent — re-tracking an already-tracked report is a no-op for
         * state and replaces the active poller.
         */
        trackPendingReport ({ commit, dispatch, state }, { conversationId, entry }) {
            if (!conversationId || !entry?.report_id) return;
            const existing = state.pendingReports[conversationId]?.[entry.report_id];
            commit('addPendingReport', { conversationId, entry });
            // Only start a poller if this is a new entry — otherwise the
            // existing poller is still running. The in-stream chunk + the
            // final-state echo will both call us; we don't want two timers.
            if (!existing) {
                dispatch('startPollingReport', { conversationId, reportId: entry.report_id });
            }
        },
        /**
         * Reconcile the per-conversation pending set with the authoritative
         * `final_state.pending_reports` list emitted at stream close. New
         * entries get pollers; entries no longer present are stopped.
         */
        reconcilePendingReports ({ commit, dispatch, state }, { conversationId, entries }) {
            if (!conversationId) return;
            const wanted = new Set();
            for (const entry of (entries || [])) {
                if (entry?.report_id) {
                    wanted.add(entry.report_id);
                    const existing = state.pendingReports[conversationId]?.[entry.report_id];
                    commit('addPendingReport', { conversationId, entry });
                    if (!existing) {
                        dispatch('startPollingReport', { conversationId, reportId: entry.report_id });
                    }
                }
            }

            // Stop pollers for anything the server says is no longer pending
            // (e.g. synthesizer already consumed the entry).
            const current = state.pendingReports[conversationId] || {};
            for (const reportId of Object.keys(current)) {
                if (!wanted.has(reportId)) {
                    dispatch('stopPollingReport', { conversationId, reportId });
                    commit('removePendingReport', { conversationId, reportId });
                }
            }
        },
        /**
         * Re-attach pollers after `loadOne()` rehydrates a conversation. Each
         * entry from `pending_reports` becomes a tracked, polled report. This
         * is what makes US3 work: user closes the tab mid-agg, reopens later,
         * we pick up where the previous tab left off.
         */
        hydratePendingReports ({ dispatch }, { conversationId, entries }) {
            if (!conversationId || !Array.isArray(entries)) return;
            for (const entry of entries) {
                if (entry?.report_id) {
                    dispatch('trackPendingReport', { conversationId, entry });
                }
            }
        },
        startPollingReport ({ dispatch, state }, { conversationId, reportId }) {
            if (!conversationId || !reportId) return;
            const key = pollKey(conversationId, reportId);
            // If a poller for this key is already running, cancel it first so
            // we don't leak timers across hydrate-then-chunk races.
            const previous = pollCancellers.get(key);
            if (previous) {
                previous();
                pollCancellers.delete(key);
            }

            const cancel = startPollingReport({
                reportId,
                onDone: ({ status, missing }) => {
                    pollCancellers.delete(key);
                    // The pending entry may have already been removed (e.g.
                    // by a reconcile from a concurrent tab). Check before
                    // firing the trigger — no entry means nothing to do.
                    if (!state.pendingReports[conversationId]?.[reportId]) return;
                    const triggerContent = status === 'success' ? 'report_done' : 'report_failed';
                    dispatch('triggerReportSynthesis', {
                        conversationId,
                        reportId,
                        triggerContent,
                        missing: missing === true,
                    });
                },
                onError: () => {
                    // Transient poll errors don't surface to the user — the
                    // next tick retries. Only 404 (handled inside the helper)
                    // terminates the loop.
                },
            });

            pollCancellers.set(key, cancel);
        },
        stopPollingReport (_ctx, { conversationId, reportId }) {
            if (!conversationId || !reportId) return;
            const key = pollKey(conversationId, reportId);
            const cancel = pollCancellers.get(key);
            if (cancel) {
                cancel();
                pollCancellers.delete(key);
            }
        },
        /**
         * POST a report_trigger message to the supervisor SSE endpoint. The
         * resulting stream is consumed by the same StreamingProcessor as a
         * normal turn; the synthesis assistant turn it produces is persisted
         * by pendo-appengine via the existing SaveConversationMessages flow.
         * See contracts/synthesis-trigger.md.
         */
        async triggerReportSynthesis (
            { commit, state, dispatch, rootState },
            { conversationId, reportId, triggerContent },
        ) {
            if (!conversationId || !reportId || !triggerContent) return;

            // Don't interrupt an in-flight stream for this conversation —
            // either a normal Leo turn or another report's synthesis. Aborting
            // it would cut off a user-visible response and (for a competing
            // synthesis) drop its pending entry. Leave this report pending and
            // let its poller re-fire once the conversation is idle; the entry
            // stays in pendingReports so the "computing…" UI persists.
            if (state.abortControllers[conversationId]) {
                dispatch('startPollingReport', { conversationId, reportId });

                return;
            }

            // Optimistically remove the entry from the pending set so the
            // "computing…" UI clears the moment we hand off to synthesis.
            // Restored on the early-out and error paths below so polling resumes.
            const previousEntry = state.pendingReports[conversationId]?.[reportId];
            commit('removePendingReport', { conversationId, reportId });

            dispatch('startLoading', conversationId);

            // No active stream (guarded above), so this invocation owns the
            // conversation's SSE for its lifetime.
            const abortController = new AbortController();
            commit('setAbortController', { conversationId, controller: abortController });

            try {
                const data = await postReportTrigger(
                    conversationId,
                    reportId,
                    triggerContent,
                    { signal: abortController.signal },
                    state.employeeConfig,
                    rootState?.auth?.user?.jobRole || '',
                    state.customInstructions || '',
                );

                if (!(data instanceof ReadableStream)) {
                    // Synthesis never started — restore the entry so the next
                    // poll tick can retry rather than silently dropping it.
                    if (previousEntry) {
                        commit('addPendingReport', { conversationId, entry: previousEntry });
                        dispatch('startPollingReport', { conversationId, reportId });
                    }

                    return;
                }

                const streamStartTime = Date.now();
                const streamId = `${conversationId}_trigger_${reportId}_${streamStartTime}`;
                commit('setActiveStreamId', { conversationId, streamId });

                const streamingProcessor = new StreamingProcessor({
                    onData: (chunk) => {
                        const currentActiveStreamId = state.activeStreamIds[conversationId];
                        if (currentActiveStreamId !== streamId) return;

                        switch (chunk.type) {
                            case 'assistant':
                                if (chunk.tool_calls?.length > 0) {
                                    dispatch('addMessage', {
                                        conversationId,
                                        type: 'assistant',
                                        content: '',
                                        data: chunk,
                                        streamId,
                                    });
                                }
                                break;
                            case 'tool':
                                dispatch('addMessage', {
                                    conversationId,
                                    type: 'tool',
                                    content: chunk.content,
                                    data: chunk,
                                    streamId,
                                });
                                break;
                            case 'pending_report':
                                // No-op: defer all handling to the done-chunk
                                // reconcile path (same reasoning as above —
                                // race between in-stream emit and proxy GCS
                                // persist).
                                break;
                        }
                    },
                    onEnd: (finalState) => {
                        const activeStreamId = state.activeStreamIds[conversationId];
                        const isActiveStream = activeStreamId === streamId;
                        if (!finalState || !isActiveStream) return;

                        commit('setConversation', {
                            id: conversationId,
                            conversation: { conversationId, ...finalState },
                        });
                        if (Array.isArray(finalState.pending_reports)) {
                            dispatch('reconcilePendingReports', {
                                conversationId,
                                entries: finalState.pending_reports,
                            });
                        }
                        commit('clearActiveStreamId', { conversationId });
                    },
                    onError: (error) => {
                        throw error;
                    },
                });

                await streamingProcessor.process(data);
            } catch (error) {
                // Restore the pending entry so the next poll tick can retry.
                if (previousEntry && error?.name !== 'AbortError') {
                    commit('addPendingReport', { conversationId, entry: previousEntry });
                    dispatch('startPollingReport', { conversationId, reportId });
                }
            } finally {
                // Only tear down if this invocation still owns the stream. A
                // newer turn or synthesis may have replaced our controller and
                // does its own cleanup; clearing here would clobber it.
                if (state.abortControllers[conversationId] === abortController) {
                    dispatch('stopLoading', conversationId);
                    commit('clearAbortController', { conversationId });
                }
            }
        },
    },
    getters: {
        awaitingFirstResponse: (state) => (id) => {
            const convo = state.conversations[id];
            const hasMessages = convo && convo.messages;
            const isFirstMessage = hasMessages && convo.messages.length === 1;

            return isFirstMessage && convo.messages[0].type === 'user' && convo.messages[0].metadata?.heading_type !== 'ask_question';
        },
        conversationById: (state) => (id) => state.conversations[id] || null,
        conversationHasMessages: (state) => (id) => {
            const convo = state.conversations[id];

            return convo && Array.isArray(convo.messages) && convo.messages.length > 0;
        },
        getArtifactConfig: (state) => (artifactId) => state.artifactConfig[artifactId] || null,
        getArtifactMenuItems: (state) => (artifactId) => state.artifactMenuItems[artifactId] || [],
        getConversationContext: (state) => (route) => {
            const conversationId = route?.name === 'LeoConversation'
                ? route.params.id
                : route?.query?.leo;

            if (!conversationId) return null;

            return state.conversations[conversationId]?.user_context;
        },
        getConversationFile: (state) => (conversationId, filePath) => {
            if (!conversationId || !filePath) return null;

            return state.conversations[conversationId]?.files?.[filePath] ?? null;
        },
        getEmployeeConfig: (state) => state.employeeConfig,
        getCustomInstructions: (state) => state.customInstructions,
        isConfigDrawerOpen: (state) => state.configDrawerOpen,
        isResponsePending: (state) => (id) => state.pendingResponses[id] || false,
        pendingReportsForConversation: (state) => (id) => {
            const bucket = state.pendingReports?.[id];
            if (!bucket) return [];

            return Object.values(bucket);
        },
        hasPendingReports: (state) => (id) => {
            const bucket = state.pendingReports?.[id];

            return !!bucket && Object.keys(bucket).length > 0;
        },
        userConversations: (state) => state.userConversations || [],
        recentConversations: (state) => state.recentConversations || [],
    },
};

export default askPendo;
